import xlsx from 'json-as-xlsx'

interface ProjectStatisticsData {
  project: {
    project_id: string
    project_name: string
    status: string
    create_time: string
    user_id: string
  } | null
  initial_prompt: {
    content: string
    contentForAi: string
    hasAssets: boolean
    assetCount: number
  } | null
  conversation_history: Array<{
    sequence: number
    role: string
    content: string
    contentForAi: string
    type: string
    hasAssets: boolean
    assetCount: number
    isTemporary: boolean
    bugfix: boolean
  }>
  coding_sessions: Array<{
    sequence: number
    message_id: string
    timestamp: number
    coins: number
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
    api_cost: number
    user_fee: number
    model: string
    vendor: string
    deduct_from: number
    deduct_to: number
  }>
  error_logs: Array<{
    timestamp: string
    error_message: string
    endpoint: string
    error_type: string
    metadata: string
  }>
  summary: {
    total_coins: number
    total_tokens: number
    total_api_cost: number
    total_user_fee: number
    total_coding_count: number
    total_error_count: number
    error_types: Record<string, number>
  }
}

// 格式化时间戳为可读的日期时间
const formatTimestamp = (timestamp: number | string): string => {
  let date: Date

  if (typeof timestamp === 'string') {
    const parsed = Number.parseInt(timestamp)
    if (Number.isNaN(parsed) || parsed <= 0) {
      return 'Invalid Date'
    }
    date = new Date(parsed)
  } else {
    if (Number.isNaN(timestamp) || timestamp <= 0) {
      return 'Invalid Date'
    }
    date = new Date(timestamp)
  }

  // 检查日期是否有效
  if (Number.isNaN(date.getTime()) || date.getTime() <= 0) {
    return 'Invalid Date'
  }

  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export async function downloadProjectStatistics(
  projectId?: string,
  userId?: string,
  fileName?: string,
) {
  try {
    // 构建查询参数
    const params = new URLSearchParams()
    if (projectId) params.append('projectId', projectId)
    if (userId) params.append('userId', userId)

    const response = await fetch(
      `/api/dashboard/project-statistics/download?${params}`,
    )

    if (!response.ok) {
      throw new Error('Failed to fetch data')
    }

    const { data }: { data: ProjectStatisticsData } = await response.json()

    // 修复重复扩展名问题
    const baseFileName =
      fileName ||
      `project-statistics-${projectId || userId}-${new Date().toISOString().split('T')[0]}`

    // 准备Excel数据，将时间戳转换为可读格式
    const xlsxData = [
      {
        sheet: 'Project Info',
        columns: [
          { label: 'Project ID', value: 'project_id' },
          { label: 'Project Name', value: 'project_name' },
          { label: 'Status', value: 'status' },
          { label: 'Create Time', value: 'create_time' },
          { label: 'User ID', value: 'user_id' },
        ],
        content: data.project ? [data.project] : [],
      },
      {
        sheet: 'Initial Prompt',
        columns: [
          { label: 'Content', value: 'content' },
          { label: 'Content For AI', value: 'contentForAi' },
          { label: 'Has Assets', value: 'hasAssets' },
          { label: 'Asset Count', value: 'assetCount' },
        ],
        content: data.initial_prompt ? [data.initial_prompt] : [],
      },
      {
        sheet: 'Conversation History',
        columns: [
          { label: 'Sequence', value: 'sequence' },
          { label: 'Role', value: 'role' },
          { label: 'Content', value: 'content' },
          { label: 'Content For AI', value: 'contentForAi' },
          { label: 'Type', value: 'type' },
          { label: 'Has Assets', value: 'hasAssets' },
          { label: 'Asset Count', value: 'assetCount' },
          { label: 'Is Temporary', value: 'isTemporary' },
          { label: 'Bug Fix', value: 'bugfix' },
        ],
        content: data.conversation_history,
      },
      {
        sheet: 'Coding Sessions',
        columns: [
          { label: 'Sequence', value: 'sequence' },
          { label: 'Message ID', value: 'message_id' },
          { label: 'Date & Time', value: 'formatted_timestamp' },
          { label: 'Coins', value: 'coins' },
          { label: 'Prompt Tokens', value: 'prompt_tokens' },
          { label: 'Completion Tokens', value: 'completion_tokens' },
          { label: 'Total Tokens', value: 'total_tokens' },
          { label: 'API Cost', value: 'api_cost' },
          { label: 'User Fee', value: 'user_fee' },
          { label: 'Model', value: 'model' },
          { label: 'Vendor', value: 'vendor' },
          { label: 'Deduct From', value: 'deduct_from' },
          { label: 'Deduct To', value: 'deduct_to' },
        ],
        content: data.coding_sessions.map((session) => ({
          ...session,
          formatted_timestamp: formatTimestamp(session.timestamp),
        })),
      },
      {
        sheet: 'Error Logs',
        columns: [
          { label: 'Date & Time', value: 'formatted_timestamp' },
          { label: 'Error Message', value: 'error_message' },
          { label: 'Endpoint', value: 'endpoint' },
          { label: 'Error Type', value: 'error_type' },
          { label: 'Metadata', value: 'metadata' },
        ],
        content: data.error_logs.map((log) => ({
          ...log,
          formatted_timestamp: formatTimestamp(log.timestamp),
        })),
      },
      {
        sheet: 'Summary',
        columns: [
          { label: 'Metric', value: 'metric' },
          { label: 'Value', value: 'value' },
        ],
        content: [
          { metric: 'Total Coins', value: data.summary.total_coins },
          { metric: 'Total Tokens', value: data.summary.total_tokens },
          { metric: 'Total API Cost', value: data.summary.total_api_cost },
          { metric: 'Total User Fee', value: data.summary.total_user_fee },
          {
            metric: 'Total Coding Count',
            value: data.summary.total_coding_count,
          },
          {
            metric: 'Total Error Count',
            value: data.summary.total_error_count,
          },
          ...Object.entries(data.summary.error_types).map(([type, count]) => ({
            metric: `Error Type - ${type}`,
            value: count,
          })),
        ],
      },
    ]

    const settings = {
      fileName: baseFileName,
    }

    return xlsx(xlsxData, settings)
  } catch (error) {
    console.error('Failed to download project statistics:', error)
    throw error
  }
}
